/**
 * SurveyForm Component
 * Displays survey questions with rating scale for respondents to complete
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './SurveyForm.css';

const SurveyForm = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Rating scale labels
  const ratingScale = [
    { value: 1, label: 'Never' },
    { value: 2, label: 'Rarely' },
    { value: 3, label: 'Sometimes' },
    { value: 4, label: 'Often' },
    { value: 5, label: 'Always' }
  ];

  useEffect(() => {
    loadSurvey();
  }, [token]);

  const loadSurvey = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const accessToken = await getAccessToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'https://demo-api.example.com/prod';
      
      const response = await fetch(`${apiUrl}/leading/api/survey-instance/${token}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Survey not found or invalid access token');
        } else if (response.status === 410) {
          throw new Error('This survey has expired');
        } else if (response.status === 403) {
          throw new Error('This survey has already been completed');
        }
        throw new Error('Failed to load survey');
      }

      const responseData = await response.json();
      // Backend returns { success: true, data: { survey: {...} } }
      const surveyData = responseData.data?.survey || responseData.survey || responseData;
      setSurvey(surveyData);
      
      // Initialize responses object
      const initialResponses = {};
      if (surveyData.questions && Array.isArray(surveyData.questions)) {
        surveyData.questions.forEach(q => {
          initialResponses[q.id] = null;
        });
      }
      setResponses(initialResponses);
      
      // Mark survey as started if it's pending
      if (surveyData.status === 'PENDING') {
        await markAsStarted(accessToken);
      }
      
    } catch (err) {
      console.error('Error loading survey:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const markAsStarted = async (accessToken) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://demo-api.example.com/prod';
      await fetch(`${apiUrl}/leading/api/survey-instance/${token}/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (err) {
      console.error('Error marking survey as started:', err);
    }
  };

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));

    // Auto-scroll to next question or submit button
    setTimeout(() => {
      const currentQuestionIndex = survey.questions.findIndex(q => q.id === questionId);
      const nextQuestionIndex = currentQuestionIndex + 1;
      
      if (nextQuestionIndex < survey.questions.length) {
        // Scroll to next question
        const nextQuestionElement = document.getElementById(`question-${survey.questions[nextQuestionIndex].id}`);
        if (nextQuestionElement) {
          nextQuestionElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      } else {
        // This was the last question, scroll to submit button
        const submitButton = document.getElementById('survey-submit-button');
        if (submitButton) {
          submitButton.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }
    }, 300); // Small delay to allow the UI to update first
  };

  const isFormComplete = () => {
    return Object.values(responses).every(response => response !== null);
  };

  const getProgress = () => {
    const answered = Object.values(responses).filter(r => r !== null).length;
    const total = Object.keys(responses).length;
    return { answered, total };
  };

  const handleSubmitClick = () => {
    if (isFormComplete()) {
      setShowConfirmModal(true);
    }
  };

  const handleConfirmSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);
      
      const accessToken = await getAccessToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'https://demo-api.example.com/prod';
      
      // Format responses for submission
      const formattedResponses = survey.questions.map(q => ({
        question_id: q.id,
        competency_id: q.competency_id,
        rating: responses[q.id]
      }));
      
      const response = await fetch(`${apiUrl}/leading/api/survey-instance/${token}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          answers: formattedResponses
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit survey');
      }

      // Survey submitted successfully - redirect to My Surveys
      alert('Survey submitted successfully! Thank you for your feedback.');
      navigate('/leading/my-surveys');
      
    } catch (err) {
      console.error('Error submitting survey:', err);
      setError(err.message);
      setShowConfirmModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="survey-form-container">
        <div className="survey-form-loading">
          <div className="spinner"></div>
          <p>Loading survey...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="survey-form-container">
        <div className="survey-form-error">
          <div className="error-icon">⚠️</div>
          <h2>Unable to Load Survey</h2>
          <p>{error}</p>
          <button 
            onClick={() => navigate('/leading/dashboard')}
            className="questionnaire-button"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!survey) {
    return null;
  }

  const progress = getProgress();
  const progressPercentage = (progress.answered / progress.total) * 100;

  // Extract template name and survey stage from survey_name
  // Formats: "BASELINE - Template", "Cohort - BASELINE - Template", "Q4 2025 - Leaders - BASELINE- Template"
  const getTemplateNameAndStage = () => {
    const stageKeywords = ['BASELINE', 'MIDPOINT', 'FINAL', 'FOLLOWUP'];
    
    // First, try to find the stage keyword in the entire string
    let foundStage = '';
    let stageIndex = -1;
    
    for (const keyword of stageKeywords) {
      // Look for the keyword with various separators (space-dash, dash, etc.)
      const regex = new RegExp(`\\s*-?\\s*${keyword}\\s*-?\\s*`, 'i');
      const match = survey.survey_name.match(regex);
      if (match) {
        foundStage = keyword.toLowerCase();
        stageIndex = match.index;
        break;
      }
    }
    
    if (foundStage && stageIndex >= 0) {
      // Extract everything after the stage keyword and its separators
      const afterStage = survey.survey_name.substring(stageIndex).replace(/^\s*-?\s*[A-Z]+\s*-?\s*/, '');
      const template = afterStage.trim();
      return { template, stage: foundStage };
    }
    
    // Fallback if no stage found
    return { template: survey.survey_name, stage: '' };
  };

  const { template, stage } = getTemplateNameAndStage();

  return (
    <div className="survey-form-container">
      {/* Survey Header */}
      <div className="survey-form-header">
        <div className="container">
          <div className="survey-header-content">
            <div>
              {/* Back to Dashboard Button */}
              <button
                onClick={() => navigate('/leading/my-surveys')}
                className="questionnaire-button back-to-dashboard-button"
                style={{
                  marginBottom: '1.5rem',
                  background: 'linear-gradient(135deg, #8d1f26 0%, #6b1f25 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(141, 31, 38, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #a02530 0%, #7d2229 100%)';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(141, 31, 38, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #8d1f26 0%, #6b1f25 100%)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(141, 31, 38, 0.3)';
                }}
              >
                ← Back to Dashboard
              </button>
              
              {/* Gallo Logo */}
              <div className="survey-logo">
                <img src="/gallou.png" alt="Gallo" style={{ height: '150px', marginBottom: '1.5rem' }} />
              </div>
              
              <h1 className="survey-title">{template}</h1>
              <p className="survey-subtitle">
                {survey.respondent_type === 'SELF' ? (
                  `${stage} - Self Assessment`
                ) : (
                  <>Providing {stage} feedback for <strong>{survey.participant_name}</strong></>
                )}
              </p>
              <p className="survey-due-date">
                Due {new Date(survey.expires_at || survey.due_date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Survey Content */}
      <div className="survey-form-content">
        <div className="container">
          {/* Rating Scale Legend - Removed for cleaner design */}

          {/* Questions */}
          <div className="survey-questions">
            {survey.questions.map((question, index) => (
              <div 
                key={question.id} 
                id={`question-${question.id}`} 
                className={`survey-question-card ${responses[question.id] !== null ? 'answered' : ''}`}
              >
                <div className="question-header">
                  <span className="question-number">Question {index + 1}</span>
                  {question.competency_name && (
                    <span className="question-competency">{question.competency_name}</span>
                  )}
                </div>
                
                <p className="question-text">{question.text}</p>
                
                <div className="rating-options">
                  {ratingScale.map(rating => (
                    <button
                      key={rating.value}
                      type="button"
                      className={`rating-option ${responses[question.id] === rating.value ? 'selected' : ''}`}
                      onClick={() => handleResponseChange(question.id, rating.value)}
                    >
                      <span className="rating-value">{rating.value}</span>
                      <span className="rating-label">{rating.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Progress Indicator */}
          <div className="survey-progress" style={{ 
            marginTop: '2rem', 
            marginBottom: '1.5rem',
            padding: '1.5rem',
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
          }}>
            <div className="progress-text" style={{ color: '#1f1f1f', fontSize: '1rem', fontWeight: '600' }}>
              Question {progress.answered} of {progress.total}
            </div>
            <div className="progress-bar" style={{ background: '#e5e5e5', height: '8px' }}>
              <div 
                className="progress-bar-fill" 
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="survey-form-actions" id="survey-submit-section">
            <button
              id="survey-submit-button"
              onClick={handleSubmitClick}
              disabled={!isFormComplete() || submitting}
              className={`questionnaire-button questionnaire-button-primary ${!isFormComplete() ? 'disabled' : ''}`}
            >
              {submitting ? 'Submitting...' : 'Submit Survey'}
            </button>
            {!isFormComplete() && (
              <p className="form-validation-message">
                Please answer all questions before submitting
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => !submitting && setShowConfirmModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Confirm Submission</h2>
            <p>
              Are you sure you want to submit your responses? 
              You will not be able to change your answers after submission.
            </p>
            <div className="modal-actions">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={submitting}
                className="questionnaire-button"
                style={{
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  textAlign: 'center',
                  padding: '0.875rem 2rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSubmit}
                disabled={submitting}
                className="questionnaire-button questionnaire-button-primary"
                style={{
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  textAlign: 'center',
                  padding: '0.875rem 2rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {submitting ? 'Submitting...' : 'Confirm Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyForm;
